import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import api from "../utils/axios"
import { API_ROUTES } from "./routes"
import { showToast } from "components/ui/toast"

// ─── Buyer Campaign Templates ───────────────────────────────────────────────

export const useGetBuyerCampaignTemplates = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_ALL_BUYER_CAMPAIGN_TEMPLATES],
    queryFn: () => api.get(API_ROUTES.GET_ALL_BUYER_CAMPAIGN_TEMPLATES),
  })
  return { data: data?.data?.data ?? [], isPending, isError, error }
}

export const useGetBuyerCampaignTemplateById = (id) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_BUYER_CAMPAIGN_TEMPLATE_BY_ID, id],
    queryFn: () =>
      api.get(`${API_ROUTES.GET_BUYER_CAMPAIGN_TEMPLATE_BY_ID}/${id}`),
    enabled: !!id,
  })
  return { data: data?.data?.data ?? null, isPending, isError, error }
}

export const useCreateBuyerCampaignTemplateMutation = () => {
  const queryClient = useQueryClient()
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (data) =>
        api.post(API_ROUTES.CREATE_BUYER_CAMPAIGN_TEMPLATE, data),
      onSuccess: () => {
        showToast("success", "Buyer campaign template created successfully")
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [API_ROUTES.GET_ALL_BUYER_CAMPAIGN_TEMPLATES],
        })
      },
    })
  return { mutate, isError, error, isLoading, isSuccess, isPending }
}

export const useUpdateBuyerCampaignTemplateMutation = () => {
  const queryClient = useQueryClient()
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (data) =>
        api.put(
          `${API_ROUTES.UPDATE_BUYER_CAMPAIGN_TEMPLATE}/${data.id}`,
          data
        ),
      onSuccess: () => {
        showToast("success", "Buyer campaign template updated successfully")
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [API_ROUTES.GET_ALL_BUYER_CAMPAIGN_TEMPLATES],
        })
      },
    })
  return { mutate, isError, error, isLoading, isSuccess, isPending }
}

export const useDeleteBuyerCampaignTemplateMutation = () => {
  const queryClient = useQueryClient()
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (id) =>
        api.delete(`${API_ROUTES.DELETE_BUYER_CAMPAIGN_TEMPLATE}/${id}`),
      onSuccess: () => {
        showToast("success", "Buyer campaign template deleted successfully")
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [API_ROUTES.GET_ALL_BUYER_CAMPAIGN_TEMPLATES],
        })
      },
    })
  return { mutate, isError, error, isLoading, isSuccess, isPending }
}

// ─── Buyer Campaigns ────────────────────────────────────────────────────────

export const useGetBuyerCampaigns = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_ALL_BUYER_CAMPAIGNS],
    queryFn: () => api.get(API_ROUTES.GET_ALL_BUYER_CAMPAIGNS),
  })
  return { data: data?.data?.data ?? [], isPending, isError, error }
}

export const useGetBuyerCampaignById = (id) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_BUYER_CAMPAIGN_BY_ID, id],
    queryFn: () =>
      api.get(`${API_ROUTES.GET_BUYER_CAMPAIGN_BY_ID}/${id}`),
    enabled: !!id,
  })
  return { data: data?.data?.data ?? null, isPending, isError, error }
}

export const useUpdateBuyerCampaignMutation = () => {
  const queryClient = useQueryClient()
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (data) =>
        api.put(`${API_ROUTES.UPDATE_BUYER_CAMPAIGN}/${data.id}`, data),
      onSuccess: () => {
        showToast("success", "Buyer campaign updated successfully")
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [API_ROUTES.GET_ALL_BUYER_CAMPAIGNS],
        })
      },
    })
  return { mutate, isError, error, isLoading, isSuccess, isPending }
}

export const useUpdateBuyerCampaignMessageMutation = () => {
  const queryClient = useQueryClient()
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: ({ id, messageId, messageContent }) =>
        api.put(`${API_ROUTES.UPDATE_BUYER_CAMPAIGN}/${id}`, { messageId, messageContent }),
      onSuccess: () => {
        showToast("success", "Message content updated successfully")
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [API_ROUTES.GET_ALL_BUYER_CAMPAIGNS],
        })
      },
    })
  return { mutate, isError, error, isLoading, isSuccess, isPending }
}

export const useDeleteBuyerCampaignMutation = () => {
  const queryClient = useQueryClient()
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (id) =>
        api.delete(`${API_ROUTES.DELETE_BUYER_CAMPAIGN}/${id}`),
      onSuccess: () => {
        showToast("success", "Buyer campaign deleted successfully")
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [API_ROUTES.GET_ALL_BUYER_CAMPAIGNS],
        })
      },
    })
  return { mutate, isError, error, isLoading, isSuccess, isPending }
}
